import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { getFollowersUsersAction, clearFollowersUsersAction, searchFollowersUsersAction, removeFollowerAction, blockUserAction, unblockUserAction, getBlockedUsersAction } from "../redux/actions/userActions";
import * as types from "../redux/constants/userConstants";
import CommonLoading from "../components/loader/CommonLoading";
import ContentWithCards from "../components/shared/ContentWithCards";
import LoadMoreButton from "../components/shared/LoadMoreButton";
import Tooltip from "../components/shared/Tooltip";
import { FaUserSlash, FaBan } from "react-icons/fa";
import { useToast } from "../contexts/ToastContext";

const SkillsCircleFollowers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [originalUsers, setOriginalUsers] = useState([]);
  const [originalTotalCount, setOriginalTotalCount] = useState(0);
  const [blockingUserId, setBlockingUserId] = useState(null);
  const followersUsers = useSelector((state) => state.user?.followersUsers);
  const totalFollowersUsers = useSelector((state) => state.user?.totalFollowersUsers);
  const blockedUsers = useSelector((state) => state.user?.blockedUsers || []);

  const LIMIT = 25;

  useEffect(() => {
    const fetchFollowersUsers = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(getFollowersUsersAction(LIMIT, 0)),
        dispatch(getBlockedUsersAction()), // Load blocked users to check status
      ]);
      setLoading(false);
    };

    fetchFollowersUsers();

    return () => {
      dispatch(clearFollowersUsersAction());
    };
  }, [dispatch, LIMIT]);

  // Store original users and total count when first loaded
  useEffect(() => {
    if (followersUsers && followersUsers.length > 0 && originalUsers.length === 0) {
      setOriginalUsers(followersUsers);
      setOriginalTotalCount(totalFollowersUsers || 0);
    }
  }, [followersUsers, originalUsers.length, totalFollowersUsers]);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        setIsSearching(true);
        dispatch(searchFollowersUsersAction(searchQuery.trim())).finally(() => {
          setIsSearching(false);
        });
      } else {
        // Restore original users when search is cleared
        if (originalUsers.length > 0) {
          dispatch({
            type: 'GET_FOLLOWERS_USERS_SUCCESS',
            payload: {
              page: 1,
              users: originalUsers,
              totalUsers: originalTotalCount,
            },
          });
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch, originalUsers, originalTotalCount]);

  const handleLoadMore = useCallback(() => {
    if (searchQuery.trim()) return; // Don't load more when searching
    
    setIsLoadMoreLoading(true);
    const currentLength = followersUsers?.length || 0;
    dispatch(getFollowersUsersAction(LIMIT, currentLength)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, LIMIT, followersUsers?.length, searchQuery]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleRemove = useCallback(async (removedUserId) => {
    try {
      const result = await dispatch(removeFollowerAction(removedUserId));
      if (result.success) {
        // Only remove from UI if API call was successful
        dispatch({
          type: types.REMOVE_FOLLOWERS_USER,
          payload: removedUserId
        });
      } else {
        console.error('Failed to remove follower:', result.error);
        // You could add a toast notification here
      }
    } catch (error) {
      console.error('Error removing follower:', error);
      // You could add a toast notification here
    }
  }, [dispatch]);

  const handleBlockUser = useCallback(async (userId) => {
    if (blockingUserId) return; // Prevent multiple clicks
    
    setBlockingUserId(userId);
    try {
      const result = await dispatch(blockUserAction(userId));
      if (result?.success) {
        showSuccess("User blocked successfully");
        // Reload followers users to update the list
        await dispatch(getFollowersUsersAction(LIMIT, 0));
      } else {
        showError(result?.error || "Failed to block user");
      }
    } catch (error) {
      showError("Error blocking user");
    } finally {
      setBlockingUserId(null);
    }
  }, [dispatch, blockingUserId, LIMIT, showSuccess, showError]);

  const handleUnblockUser = useCallback(async (userId) => {
    if (blockingUserId) return; // Prevent multiple clicks
    
    setBlockingUserId(userId);
    try {
      const result = await dispatch(unblockUserAction(userId));
      if (result?.success) {
        showSuccess("User unblocked successfully");
      } else {
        showError(result?.error || "Failed to unblock user");
      }
    } catch (error) {
      showError("Error unblocking user");
    } finally {
      setBlockingUserId(null);
    }
  }, [dispatch, blockingUserId, showSuccess, showError]);

  const handleUserClick = useCallback((user) => {
    const profileUrl = user?.profileUrl || user?._id;
    if (profileUrl) {
      navigate(`/profile/${profileUrl}`);
    }
  }, [navigate]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Current displayed users (for UI) with navigation functionality
  const displayedUsers = useMemo(() => {
    return (followersUsers || []).map((user) => {
      return (
        <div 
          key={user._id}
          className="bg-white border rounded-md w-full shadow-2xl shadow-[#f2f5fc] relative hover:shadow-lg hover:border-primary-300 transition-all duration-300 cursor-pointer"
          onClick={() => handleUserClick(user)}
        >
          {/* User Details Header Card */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="hover:bg-gray-50 rounded-md p-3 -m-3 transition-colors">
              {/* Top row with avatar, name, and action buttons */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <img
                    src={user.avatar}
                    alt="Avatar"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <h4 className="font-semibold text-gray-800 font-heading text-base">{user.name}</h4>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip text="Remove">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(user._id);
                      }}
                      className="p-2 text-action/80 hover:text-action hover:bg-action/10 rounded-full transition-colors"
                    >
                      <FaUserSlash className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  {blockedUsers.includes(user._id) ? (
                    <Tooltip text="Unblock">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnblockUser(user._id);
                        }}
                        disabled={blockingUserId === user._id}
                        className="p-2 text-primary-700 hover:text-primary-800 hover:bg-primary-50 rounded-full transition-colors disabled:opacity-50"
                      >
                        <FaBan className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  ) : (
                    <Tooltip text="Block">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBlockUser(user._id);
                        }}
                        disabled={blockingUserId === user._id}
                        className="p-2 text-action/80 hover:text-action hover:bg-action/10 rounded-full transition-colors disabled:opacity-50"
                      >
                        <FaBan className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
              
              {/* Content below */}
              <div className="ml-13 pl-1">
                <span className="text-sm text-gray-600 font-medium font-flex-regular">
                  Following Since: {new Date(user.followingSince).toLocaleString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [followersUsers, handleRemove, handleUserClick, handleBlockUser, handleUnblockUser, blockedUsers, blockingUserId]);

  // Determine if we should show load more button
  const shouldShowLoadMore = useMemo(() => {
    if (searchQuery.trim()) return false; // Don't show during search
    if (!followersUsers || followersUsers.length === 0) return false; // No users to show
    if (isSearching) return false; // Don't show while searching
    
    // Use original total count for comparison
    const currentCount = followersUsers?.length || 0;
    const totalCount = originalTotalCount || 0;
    
    // Only show Load More if there are more users to load AND total is greater than LIMIT
    return currentCount < totalCount && totalCount > LIMIT;
  }, [searchQuery, followersUsers, isSearching, originalTotalCount, LIMIT]);

  return (
    <ContentWithCards>
      <div className="main-section bg-white border">
        <div className="p-3">
          {loading ? (
            <div className="flex items-center justify-center h-screen">
              <CommonLoading />
            </div>
          ) : (
            <div>
              {/* Title and Tabs */}
              <div className="mb-4 border-b py-2 pl-2 pr-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-700">
                    My Skills Circle - Followers
                  </h2>
                  
                  {/* Tabs */}
                  <div className="flex gap-2">
                    <Link
                      to="/skills-circle/followers"
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                        location.pathname === "/skills-circle/followers"
                          ? "bg-primary-700 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Followers
                    </Link>
                    <Link
                      to="/skills-circle/following"
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                        location.pathname === "/skills-circle/following"
                          ? "bg-primary-700 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Following
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Search Bar */}
              <div className="mb-6 pl-2 pr-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search Followers List"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full px-4 py-3 pl-10 pr-4 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder-gray-500 font-roboto-flex"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  {isSearching && (
                    <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Search results count */}
              {searchQuery && (
                <div className="mb-4 text-center">
                  <p className="text-sm text-gray-600 font-roboto-flex">
                    {isSearching 
                      ? `Searching for "${searchQuery}"...`
                      : followersUsers?.length === 0 
                        ? `No results found for "${searchQuery}"`
                        : `${followersUsers.length} result${followersUsers.length !== 1 ? 's' : ''} found for "${searchQuery}"`
                    }
                  </p>
                </div>
              )}
              
              {followersUsers?.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start px-2">
                    {displayedUsers}
                  </div>
                  
                  {/* Load More Button */}
                  {shouldShowLoadMore && (
                    <LoadMoreButton
                      onClick={handleLoadMore}
                      isLoading={isLoadMoreLoading}
                      text={`Load More Users (${followersUsers?.length || 0}/${totalFollowersUsers || 0})`}
                    />
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  {searchQuery ? (
                    <>
                      <p className="text-gray-500 font-roboto-flex mb-4">
                        No followers match your search criteria
                      </p>
                      <button
                        onClick={clearSearch}
                        className="text-primary-700 hover:text-primary-800 font-medium font-roboto-flex"
                      >
                        Clear search
                      </button>
                    </>
                  ) : (
                    <div className="flex justify-center items-center flex-col">
                      <p className="text-gray-500 py-4 font-roboto-flex">
                        You don't have any followers yet.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ContentWithCards>
  );
};

export default SkillsCircleFollowers;
